import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { cn } from 'src/utils/style'

export interface ActionMenuItem {
  key: string
  label: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  tone?: 'neutral' | 'error'
  testId?: string
}

interface ActionMenuProps {
  items: ActionMenuItem[]
  /** Accessible label for the trigger button. */
  label: string
  testId?: string
  className?: string
}

/** Overflow ("kebab") menu: an icon trigger that expands a vertical list of actions. */
export const ActionMenu: FC<ActionMenuProps> = ({ items, label, testId, className }) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [isOpen])

  if (items.length === 0) return null

  return (
    <div ref={ref} className={cn(['relative', className])}>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        icon={<EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />}
        tooltip={isOpen ? undefined : label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen((open) => !open)
        }}
        data-testid={testId}
      />
      {isOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 min-w-[12rem] rounded-lg border border-border bg-surface py-1 shadow-lg"
        >
          {items.map((item) => (
            <Button
              key={item.key}
              role="menuitem"
              variant="ghost"
              tone={item.tone}
              size="sm"
              icon={item.icon}
              disabled={item.disabled}
              className="w-full justify-start gap-2 rounded-none"
              onClick={(e) => {
                e.stopPropagation()
                setIsOpen(false)
                item.onClick()
              }}
              data-testid={item.testId}
            >
              {item.label}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
